import type { Cadence } from './reportEnums.constants';
import { formatReportMonthLabel } from './reportPeriodComposition.util';

/** Parsed month picker values for report metadata forms. */
export interface ParsedReportingPeriodPickers {
  startMonth: string;
  endMonth: string;
}

const QUARTERLY_PERIOD_PATTERN =
  /^Q([1-4])\s+(\d{4})\s+\(([^)]+)\s+[-–]\s+([^)]+)\)$/u;

const REPORTING_PERIOD_RANGE_SEPARATOR = /\s+[-–]\s+/u;

/**
 * Finds a `YYYY-MM` key whose formatted label matches the given display label.
 * @param label - Human-readable month label such as `Apr 2026`.
 * @param availableMonths - Customer month keys.
 * @returns Matching month key or empty string.
 */
const resolveMonthKeyFromLabel = (
  label: string,
  availableMonths: string[],
): string => {
  const normalized = label.trim();
  const match = availableMonths.find(
    monthKey => formatReportMonthLabel(monthKey) === normalized,
  );
  return match ?? '';
};

/**
 * Parses a stored reporting period into month picker values for edit forms.
 * @param cadence - Report cadence.
 * @param reportingPeriod - Stored reporting period label.
 * @param availableMonths - Customer month keys for label resolution.
 * @returns Start/end month keys when parseable.
 */
export const parseReportingPeriodForPickers = (
  cadence: Cadence,
  reportingPeriod: string,
  availableMonths: string[],
): ParsedReportingPeriodPickers => {
  if (cadence === 'adhoc' || !reportingPeriod.trim()) {
    return { startMonth: '', endMonth: '' };
  }

  if (cadence === 'monthly') {
    const parts = reportingPeriod.split(REPORTING_PERIOD_RANGE_SEPARATOR).map(part => part.trim());
    if (parts.length === 1) {
      const endMonth = resolveMonthKeyFromLabel(parts[0] ?? '', availableMonths);
      return { startMonth: '', endMonth };
    }
    return {
      startMonth: resolveMonthKeyFromLabel(parts[0] ?? '', availableMonths),
      endMonth: resolveMonthKeyFromLabel(parts[1] ?? '', availableMonths),
    };
  }

  const quarterlyMatch = reportingPeriod.match(QUARTERLY_PERIOD_PATTERN);
  if (quarterlyMatch) {
    const startLabel = quarterlyMatch[3] ?? '';
    const endLabel = quarterlyMatch[4] ?? '';
    return {
      startMonth: resolveMonthKeyFromLabel(startLabel, availableMonths),
      endMonth: resolveMonthKeyFromLabel(endLabel, availableMonths),
    };
  }

  return { startMonth: '', endMonth: '' };
};
